import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {useController, useForm} from 'react-hook-form';
import {useLuxonTimezone, useStale} from 'hooks';
import produce from 'immer';

import './UpdateInfoModal.scss';
import {UpdatePageModel} from '../UpdatePageModel';
import {Alert, ErrorMessage, FormHeading, FormInput, FormSelect, FormTextarea} from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import {AsyncStatus, UpdateState} from 'enums';
import {IUpdateFile, IUpdateType} from 'interfaces';
import {ManufacturersService, parseAxiosError, UpdateFilesService, UpdateTypesService, UsersService} from 'services';
import { formatDateAndTime } from 'services/Utils';

interface IUpdateInfoModalProps {
    canEdit: boolean,
    model: UpdatePageModel,
    onHide: () => void,
    show: boolean,
}

interface IUpdateInfoForm {
    deviceModelIds: number[],
    manufacturerId: string,
    name: string,
    notes: string,
    updateTypeId: string,
    updateFileId: string,
}

interface IUpdateInfoModalState {
    saveError?: string,
    saveStatus: AsyncStatus,
    updateFiles: IUpdateFile[],
    updateFilesError?: string,
    updateFilesStatus: AsyncStatus,
}

const errRequireManufacturer = 'This field is required if Manufacturer is set';

export const UpdateInfoModal: FunctionComponent<IUpdateInfoModalProps> = function UpdateInfoModal(props) {
    if (props.model.loadStatus !== AsyncStatus.Success) return null;

    const { canEdit, model, onHide, show } = props;
    const [state, setState] = useState<IUpdateInfoModalState>({
        saveStatus: AsyncStatus.Initial,
        updateFiles: [],
        updateFilesStatus: AsyncStatus.Initial,
    })
    const { saveError, saveStatus, updateFiles, updateFilesError, updateFilesStatus } = state;
    const { hasTimezone, timezone } = useLuxonTimezone(UsersService.me.timezone);

    // ------------------ Form Stuff ------------------

    const form = useForm<IUpdateInfoForm>({
        mode: 'onTouched',
        defaultValues: {
            deviceModelIds: model.update.deviceModelIds.slice(), // Copy the array so we don't modify the orig.
            manufacturerId: model.update.manufacturerId + '',
            name: model.update.name,
            notes: model.update.notes,
            updateTypeId: model.update.updateTypeId + '',
            updateFileId: model.update.updateFileId + '',
        },
    });
    
    // React hook form will only track dirty fields if we access the property.
    // It's an optimization on their part, but it feels side-effecty to have to do this.
    const dirtyFields = form.formState.dirtyFields;

    // Get the current values from the form for these fields.
    // Normal react-hook-form fields are uncontrolled, but we need to rerender when these change.
    const valManufacturerId = form.watch('manufacturerId');
    const valUpdateTypeId = form.watch('updateTypeId');
    const valUpdateFileId = form.watch('updateFileId');
    const valDeviceModelIds = form.watch('deviceModelIds');

    // We need custom form handling for the array of selected models
    const modelsCtrl = useController({
        control: form.control,
        name: "deviceModelIds",
        rules: {
            validate: (v) => {
                // Only require this field if manufacturer is set
                if (!valManufacturerId) return true;
                return v?.length > 0
            },
        },
    });

    // Compute derived data. We're populating updateTypes/updateFile/models based on properties in the update.
    let updateTypes: IUpdateType[] = [];
    if (valManufacturerId) {
        updateTypes = UpdateTypesService.updateTypes.filter(ut => (ut.manufacturerId + '') === valManufacturerId);
    }

    const updateFile = valUpdateFileId && updateFiles.find(uf => (uf.id + '') === valUpdateFileId);

    const selectedModels = {};
    if (valDeviceModelIds) {
        for (const id of valDeviceModelIds) {
            selectedModels[id] = true;
        }
    }

    // ------------------ Data loading/saving ------------------


    // Keep the updateFiles list in sync with the selected updateType
    useEffect(() => {
        if (!show) return;
    
        if (!valUpdateTypeId) {
            setState(produce(draft => {
                draft.updateFiles = [];
                draft.updateFilesError = undefined;
                draft.updateFilesStatus = AsyncStatus.Success;
            }))
            return;
        }
        
        // check filtered list
        let newUpdateFiles = UpdateFilesService.allUpdateFiles
            .filter(uf => uf.updateTypeId === Number(valUpdateTypeId));

        setState(produce(draft => {
            draft.updateFiles = newUpdateFiles;
            draft.updateFilesStatus = AsyncStatus.Success;
        }));
    }, [show, valUpdateTypeId])

    // After we load for the first time, we need to reset the updateFileId field
    // because react-hook-form cleared it because the dropdown was initially empty.
    const updateFiles_firstLoadRef = useRef(true);
    useEffect(() => {
        if (!show) return;

        if (updateFilesStatus === AsyncStatus.Success && updateFiles_firstLoadRef.current) {
            updateFiles_firstLoadRef.current = false;
            form.setValue('updateFileId', model.update.updateFileId);
        }
    }, [show, updateFilesStatus]);

    // ------------------ Callbacks ------------------

    function onChangeManufacturer(e) {
        // Since the user changed the manufacturer, we need to clear dependent fields.
        form.setValue('updateTypeId', undefined);
        form.setValue('updateFileId', undefined);
        form.setValue('deviceModelIds', []);

        // Redo field validation for dependent fields if they've been touched
        const fields: Array<keyof IUpdateInfoForm> = [
            'updateTypeId',
            'updateFileId',
            'deviceModelIds',
        ];
        for (const f of fields) {
            if (dirtyFields[f]) form.trigger(f);
        }
    }

    function onChangeUpdateType(e) {
        // Since the user changed the updateType, we need to clear dependent fields.
        form.setValue('updateFileId', undefined);
        form.setValue('deviceModelIds', []);

        // Redo field validation for dependent fields if they've been touched
        const fields: Array<keyof IUpdateInfoForm> = [
            'updateFileId',
            'deviceModelIds',
        ];
        for (const f of fields) {
            if (dirtyFields[f]) form.trigger(f);
        }
    }

    function onChangeUpdateFile(e) {
        // Since the user changed the updateType, we need to clear dependent fields.
        form.setValue('deviceModelIds', []);

        // Redo field validation for dependent fields if they've been touched
        const fields: Array<keyof IUpdateInfoForm> = [
            'deviceModelIds',
        ];
        for (const f of fields) {
            if (dirtyFields[f]) form.trigger(f);
        }
    }

    function onChangeModelCheckbox(e, modelId: number) {
        // Modify the value array, then call our controller's onChange callback
        const modelIds = modelsCtrl.field.value;
        const index = modelIds.indexOf(modelId)
        if (e.target.checked) {
            // Only add if it's not already in the array
            if (index === -1) {
                modelIds.push(modelId);
            } else {
                console.warn('Not adding duplicate deviceModelId')
            }
        } else {
            if (index !== -1) {
                modelIds.splice(index, 1);
            } else {
                console.warn('Couldnt find deviceModelId to remove from list')
            }
        }
        modelsCtrl.field.onChange(modelIds);
    }

    function onSubmitValid(vals: IUpdateInfoForm) {
        if (!canEdit || saveStatus === AsyncStatus.Loading) return;

        setState(produce(draft => {
            draft.saveStatus = AsyncStatus.Loading;
            draft.saveError = '';
        }))

        // Copy our form values onto the update, then save.
        model.update.name = vals.name;
        model.update.notes = vals.notes;
        model.update.manufacturerId = Number(vals.manufacturerId) || 0;
        model.update.updateTypeId = Number(vals.updateTypeId) || 0;
        model.update.updateFileId = Number(vals.updateFileId) || 0;
        model.update.deviceModelIds = vals.deviceModelIds;

        model.save()
            .then(onHide)
            .catch((err) => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Error;
                    draft.saveError = model.apiCallError;
                }))
            });
    }

    return (
        <Modal show={show} onClose={onHide} size="lg" className="updateInfoModal">
            <form onSubmit={form.handleSubmit(onSubmitValid)}>
                <ModalHeader>
                    Update Info
                </ModalHeader>
                <ModalBody style={{textAlign: "left"}}>
                    <div className="row">
                        <div className="col-6">
                            <FormInput
                                form={form}
                                label="Name"
                                maxLength={128}
                                name="name"
                                registerOptions={{required: true}}
                                disabled={!canEdit}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-4">
                            <FormSelect
                                form={form}
                                getOptionLabel={m => m.name}
                                getOptionValue={m => m.id}
                                label="Manufacturer"
                                name="manufacturerId"
                                options={ManufacturersService.manufacturers}
                                disabled={!canEdit}
                                onChange={onChangeManufacturer}
                            />
                        </div>
                        <div className="col-4">
                            <FormSelect
                                form={form}
                                getOptionLabel={ut => ut.description}
                                getOptionValue={ut => ut.id}
                                label="Update Type"
                                name="updateTypeId"
                                options={updateTypes}
                                disabled={!canEdit}
                                onChange={onChangeUpdateType}
                                registerOptions={{
                                    validate: {
                                        required: v => {
                                            // We need to require this field if "Manufacturer" has been set
                                            if (valManufacturerId) {
                                                return (parseInt(v, 10) > 0) ? true : errRequireManufacturer;
                                            }
                                            return true;
                                        },
                                    }
                                }}
                            />
                        </div>
                        <div className="col-4">
                            {/* TODO - Create a fancy dropdown so we can show the description */}
                            <FormSelect
                                form={form}
                                getOptionLabel={uf => uf.version}
                                getOptionValue={uf => uf.id}
                                label="Update Version"
                                name="updateFileId"
                                options={updateFiles}
                                disabled={!canEdit}
                                onChange={onChangeUpdateFile}
                                registerOptions={{
                                    validate: {
                                        required: v => {
                                            // We need to require this field if "Manufacturer" has been set
                                            if (valManufacturerId) {
                                                return (parseInt(v, 10) > 0) ? true : errRequireManufacturer;
                                            }
                                            return true;
                                        },
                                    }
                                }}
                            />
                            {updateFilesError && (
                                <div className="updateInfoModal_updateFilesError">
                                    {updateFilesError}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-4">
                            <div className="formItem">
                                <div className="formItem_label">
                                    Targeted Device Models
                                </div>
                                <ul className="updateInfoModal_models">
                                    {updateFile && updateFile.deviceModelIds.map((id, i) => {
                                        const deviceModel = ManufacturersService.modelsMap[id];
                                        if (!deviceModel) return null;

                                        return (
                                            <li className="updateInfoModal_model" key={id}>
                                                <label>
                                                    {/* For "checked", we have to cast to boolean to make sure the property is still passed if false */}
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selectedModels[id]}
                                                        disabled={!canEdit}
                                                        onChange={(e) => onChangeModelCheckbox(e, id)}
                                                        onBlur={modelsCtrl.field.onBlur}
                                                    />
                                                    {deviceModel.name}
                                                </label>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        </div>
                        <div className="col-8">
                            {updateFile && (<>
                                <div className="formItem_label">
                                    Update Version Description:
                                </div>
                                <div className="updateInfoModal_firmwareInfoValue">
                                    {updateFile?.description}
                                </div>
                                <div className="formItem_label">
                                    Created At:
                                </div>
                                <div className="updateInfoModal_firmwareInfoValue">
                                    {updateFile?.createdAt && formatDateAndTime(updateFile?.createdAt, timezone, hasTimezone)} by {updateFile?.createdBy?.name}
                                </div>
                            </>)}
                        </div>
                    </div>
                    {form.errors.deviceModelIds && (<>
                        <Alert type="danger">Please select at least 1 targeted device model.</Alert>
                    </>)}
                    <div className="row">
                        <div className="col">
                            <FormTextarea
                                form={form}
                                label="Notes"
                                maxLength={2000}
                                name="notes"
                                rows={3}
                                disabled={!canEdit}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormHeading>
                                History
                            </FormHeading>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <table className="updateInfoModal_historyTable">
                                <tbody>
                                    <tr>
                                        <td>Created by:</td>
                                        <td>{model.update.createdBy?.name}</td>
                                        <td>{formatDateAndTime(model.update.createdAt, timezone, hasTimezone)}</td>
                                    </tr>
                                    {model.update.state >= UpdateState.AwaitApproval && (
                                        <tr>
                                            <td>Submitted by:</td>
                                            <td>{model.update.submittedBy?.name}</td>
                                            <td>{formatDateAndTime(model.update.submittedAt, timezone, hasTimezone)}</td>
                                        </tr>
                                    )}
                                    {model.update.state >= UpdateState.Scheduled && (
                                        <tr>
                                            <td>Approved by:</td>
                                            <td>{model.update.approvedBy?.name}</td>
                                            <td>{formatDateAndTime(model.update.approvedAt, timezone, hasTimezone)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Last modified by:</td>
                                        <td>{model.update.modifiedBy?.name}</td>
                                        <td>{formatDateAndTime(model.update.modifiedAt, timezone, hasTimezone)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {saveError && <ErrorMessage message={saveError} />}
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="outlined"
                        onClick={onHide}
                        disabled={saveStatus === AsyncStatus.Loading}
                    >
                        Close
                    </Button>
                    <Button
                        style={{marginLeft: "10px"}}
                        type="submit"
                        loading={saveStatus === AsyncStatus.Loading}
                        disabled={!canEdit || saveStatus === AsyncStatus.Success}
                    >
                        {saveStatus === AsyncStatus.Loading && 'Saving'}
                        {saveStatus !== AsyncStatus.Loading && 'Save'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
}